/**
 * REPO PATH:
 *   apps/web/src/components/ReviewerFeedbackForm.tsx
 *
 * Client island. Imported from the /reviewer-guide server page.
 * Posts to /api/reviewer-feedback. Mirrors server-side validation
 * (length caps, email format, severity enum). Inline-CSS styling
 * matching the rest of the platform (var(--mcc-deep) / var(--mcc-navy)).
 */
'use client';

import { useMemo, useState, type CSSProperties } from 'react';

const SEVERITY_OPTIONS = [
  { value: 'OBSERVATION', label: 'Observation — neutral note' },
  { value: 'SUGGESTION',  label: 'Suggestion — improvement idea' },
  { value: 'CONCERN',     label: 'Concern — should be addressed' },
  { value: 'BLOCKER',     label: 'Blocker — must be addressed before pilot' },
] as const;

type Severity = typeof SEVERITY_OPTIONS[number]['value'];

export interface ReviewerFeedbackFormProject {
  id: string;
  name: string;
  family: string;
  status: string;
}

interface Props {
  projects: ReviewerFeedbackFormProject[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FEEDBACK_MAX = 5000;

const inputBase: CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  fontSize: 15,
  border: '1px solid var(--mcc-border)',
  borderRadius: 6,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  backgroundColor: 'var(--mcc-surface)',
  color: 'var(--mcc-ink)',
};

const labelBase: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--mcc-deep)',
  marginBottom: 6,
  letterSpacing: '0.2px',
};

export default function ReviewerFeedbackForm({ projects }: Props) {
  const [name, setName]           = useState('');
  const [email, setEmail]         = useState('');
  const [org, setOrg]             = useState('');
  const [projectId, setProjectId] = useState<string>('');
  const [topic, setTopic]         = useState('');
  const [severity, setSeverity]   = useState<Severity>('OBSERVATION');
  const [feedback, setFeedback]   = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const dropdownOptions = useMemo(
    () => [
      { id: '', label: 'General — not project-specific' },
      ...projects.map((p) => ({
        id: p.id,
        label: `${p.name} (${p.family} · ${p.id})`,
      })),
    ],
    [projects]
  );

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!name.trim())                 errs.name = 'Required';
    else if (name.trim().length > 200) errs.name = 'Max 200 characters';
    if (!email.trim())                  errs.email = 'Required';
    else if (!EMAIL_RE.test(email))     errs.email = 'Invalid email format';
    else if (email.trim().length > 320) errs.email = 'Max 320 characters';
    if (org.trim().length > 200)        errs.org = 'Max 200 characters';
    if (!topic.trim())                  errs.topic = 'Required';
    else if (topic.trim().length > 200) errs.topic = 'Max 200 characters';
    if (!feedback.trim())               errs.feedback = 'Required';
    else if (feedback.trim().length > FEEDBACK_MAX) errs.feedback = `Max ${FEEDBACK_MAX} characters`;
    return errs;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError('Please correct the highlighted fields.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/reviewer-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviewer_name: name.trim(),
          reviewer_email: email.trim(),
          reviewer_org: org.trim(),
          project_id: projectId || null,
          topic: topic.trim(),
          severity,
          feedback: feedback.trim(),
          reviewer_locale: typeof navigator !== 'undefined' ? navigator.language : null,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data?.error || 'Submission failed. Please try again.');
        setSubmitting(false);
        return;
      }

      setSuccess('Thank you. Your feedback has been recorded.');
      setName('');
      setEmail('');
      setOrg('');
      setProjectId('');
      setTopic('');
      setSeverity('OBSERVATION');
      setFeedback('');
      setFieldErrors({});
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const remaining = FEEDBACK_MAX - feedback.length;

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: 'white',
        border: '1px solid var(--mcc-border)',
        borderRadius: 12,
        padding: 28,
        display: 'grid',
        gap: 18,
        boxShadow: '0 1px 3px rgba(11,58,91,0.06)',
      }}
    >
      {success && (
        <div role="status" style={{ background: 'var(--mcc-success-bg)', border: '1px solid #6ee7b7', color: 'var(--mcc-success-fg)', padding: '12px 14px', borderRadius: 6, fontSize: 14, fontWeight: 500 }}>
          {success}
        </div>
      )}
      {error && (
        <div role="alert" style={{ background: 'var(--mcc-danger-bg)', border: '1px solid #fecaca', color: 'var(--mcc-danger-fg)', padding: '12px 14px', borderRadius: 6, fontSize: 14, fontWeight: 500 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        <div>
          <label htmlFor="rf-name" style={labelBase}>Name *</label>
          <input
            id="rf-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            required
            autoComplete="name"
            style={{ ...inputBase, borderColor: fieldErrors.name ? 'var(--mcc-danger-fg)' : inputBase.border as string }}
          />
          {fieldErrors.name && <div style={{ fontSize: 12, color: 'var(--mcc-danger-fg)', marginTop: 4 }}>{fieldErrors.name}</div>}
        </div>

        <div>
          <label htmlFor="rf-email" style={labelBase}>Email *</label>
          <input
            id="rf-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={320}
            required
            autoComplete="email"
            style={{ ...inputBase, borderColor: fieldErrors.email ? 'var(--mcc-danger-fg)' : inputBase.border as string }}
          />
          {fieldErrors.email && <div style={{ fontSize: 12, color: 'var(--mcc-danger-fg)', marginTop: 4 }}>{fieldErrors.email}</div>}
        </div>
      </div>

      <div>
        <label htmlFor="rf-org" style={labelBase}>Organisation</label>
        <input
          id="rf-org"
          type="text"
          value={org}
          onChange={(e) => setOrg(e.target.value)}
          maxLength={200}
          autoComplete="organization"
          placeholder="e.g. University of Tunis"
          style={{ ...inputBase, borderColor: fieldErrors.org ? 'var(--mcc-danger-fg)' : inputBase.border as string }}
        />
        {fieldErrors.org && <div style={{ fontSize: 12, color: 'var(--mcc-danger-fg)', marginTop: 4 }}>{fieldErrors.org}</div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        <div>
          <label htmlFor="rf-project" style={labelBase}>Project</label>
          <select
            id="rf-project"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            style={{ ...inputBase, cursor: 'pointer' }}
          >
            {dropdownOptions.map((opt) => (
              <option key={opt.id || 'general'} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="rf-severity" style={labelBase}>Severity *</label>
          <select
            id="rf-severity"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as Severity)}
            required
            style={{ ...inputBase, cursor: 'pointer' }}
          >
            {SEVERITY_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="rf-topic" style={labelBase}>Topic *</label>
        <input
          id="rf-topic"
          type="text"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          maxLength={200}
          required
          placeholder="e.g. Additionality argument too thin in section 4"
          style={{ ...inputBase, borderColor: fieldErrors.topic ? 'var(--mcc-danger-fg)' : inputBase.border as string }}
        />
        {fieldErrors.topic && <div style={{ fontSize: 12, color: 'var(--mcc-danger-fg)', marginTop: 4 }}>{fieldErrors.topic}</div>}
      </div>

      <div>
        <label htmlFor="rf-feedback" style={labelBase}>Feedback *</label>
        <textarea
          id="rf-feedback"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value.slice(0, FEEDBACK_MAX))}
          maxLength={FEEDBACK_MAX}
          required
          rows={8}
          placeholder="What did you find? Be specific — references to sections, screenshots welcome (paste links). The team reads everything."
          style={{ ...inputBase, resize: 'vertical', minHeight: 160, borderColor: fieldErrors.feedback ? 'var(--mcc-danger-fg)' : inputBase.border as string }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
          {fieldErrors.feedback
            ? <div style={{ fontSize: 12, color: 'var(--mcc-danger-fg)' }}>{fieldErrors.feedback}</div>
            : <div style={{ fontSize: 12, color: 'var(--mcc-muted)' }}>Up to {FEEDBACK_MAX.toLocaleString('en-GB')} characters</div>}
          <div style={{ fontSize: 12, color: remaining < 200 ? 'var(--mcc-warn-fg)' : 'var(--mcc-muted)' }}>{remaining.toLocaleString('en-GB')} left</div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: 'var(--mcc-muted)', maxWidth: 420 }}>
          By submitting, you agree your name, email and (optional) institution may be retained for follow-up.
          We do not publish your contact details.
        </div>
        <button
          type="submit"
          disabled={submitting}
          style={{
            padding: '12px 26px',
            backgroundColor: submitting ? '#7c93a8' : 'var(--mcc-deep)',
            color: 'white',
            fontSize: 15,
            fontWeight: 600,
            border: 'none',
            borderRadius: 6,
            cursor: submitting ? 'not-allowed' : 'pointer',
            letterSpacing: '0.3px',
          }}
        >
          {submitting ? 'Sending…' : 'Submit feedback'}
        </button>
      </div>
    </form>
  );
}
